import { useState, useEffect } from 'react'
import { Button, Col, Form, Row, Badge, Modal } from 'react-bootstrap'
import { createIncident, getNextCaseId, getAssignableUsers, getActiveIncidentCategories, uploadIncidentAttachment, getIncidentRateLimitStatus } from '../../../api/IncidentAPI'
// import { getUsers } from '../../../api/AdminAPI'

interface IncidentFormProps {
  onTabChange: (tab: string) => void
  onSuccess?: () => void
}

const IncidentForm = ({ onTabChange, onSuccess }: IncidentFormProps) => {
  
  const [formData, setFormData] = useState({
    caseId: '',
    categoryCode: '',
    incidentDate: '',
    description: '',
    peopleInvolved: '',
    locationContext: '',
    isAnonymous: false,
    priority: 'MEDIUM',
    assignedToId: '',
  })
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)
  const [declarationError, setDeclarationError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [users, setUsers] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [attachments, setAttachments] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('Concern raised successfully.')
  const [declarationAccepted, setDeclarationAccepted] = useState(false)
  const [descriptionError, setDescriptionError] = useState<string | null>(null)

  // Get today's date in YYYY-MM-DD format for max attribute
  const today = new Date().toISOString().split('T')[0]

  // Fetch next Case ID when form loads
    useEffect(() => {
  const fetchInitialData = async () => {
    try {
      // Fetch next Case ID
      const caseResponse = await getNextCaseId()

      if (caseResponse.statusCode === 200) {
        setFormData(prev => ({
          ...prev,
          caseId: caseResponse.data.caseId,
        }))
      }

      // Fetch users
      const userResponse = await getAssignableUsers()
      console.log('ASSIGNABLE USERS:', userResponse)
      if (userResponse.statusCode === 200) {
        setUsers(userResponse.data?.items || [])
      }

      // Fetch active categories
      const categoryResponse = await getActiveIncidentCategories()
      if (categoryResponse.statusCode === 200) {
        setCategories(categoryResponse.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch form data:', error)
      setError('Failed to load form data')
    }
  }

  fetchInitialData()
}, [])

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value, type, checked } = e.target

    // Validate incident date - only allow today or past dates
    if (name === 'incidentDate' && value) {
      const selectedDate = new Date(value + 'T00:00:00')
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison

      if (selectedDate > today) {
        setDateError('Incident date cannot be in the future. Please select today or a past date.')
        setFormData(prev => ({
          ...prev,
          [name]: value,
        }))
        return
      } else {
        setDateError(null)
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setDateError(null)
    setDeclarationError(null)
    setFileError(null)
    setDescriptionError(null)

    // Basic validation
    if (!formData.caseId || !formData.categoryCode || !formData.incidentDate || !formData.description) {
      setError('Please fill in all required fields')
      return
    }

    const description = formData.description.trim()
    if (description.length < 50) {
      setDescriptionError('Description must contain at least 50 non-whitespace characters.')
      return
    }

    // Date validation - check if date is in the future
    if (formData.incidentDate) {
      const selectedDate = new Date(formData.incidentDate + 'T00:00:00')
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (selectedDate > today) {
        setDateError('Incident date cannot be in the future. Please select today or a past date.')
        return
      }
    }

    // Declaration validation
    if (!declarationAccepted) {
      setDeclarationError('Please confirm that the information provided is accurate to the best of your knowledge.')
      return
    }

    try {
      const limitResponse = await getIncidentRateLimitStatus()
      if (limitResponse.statusCode !== 200) {
        setError(limitResponse.message || 'Unable to validate submission limit')
        return
      }
      if (!limitResponse.data?.allowed) {
        setError('Rate limit exceeded')
        return
      }
      setShowConfirmModal(true)
    } catch (limitError: any) {
      setError(limitError.message || 'Unable to validate submission limit')
    }
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false)
    setLoading(true)
    try {
      const response = await createIncident({
        ...formData,
        description: formData.description.trim(),
        incidentDate: formData.incidentDate,
      })
      
      if (response.statusCode === 201) {
        const incidentId = response.data.id

        // Upload attachments if any
        if (attachments.length > 0) {
          const uploadPromises = attachments.map(file =>
            uploadIncidentAttachment(incidentId, file)
          )

          const uploadResults = await Promise.all(uploadPromises)
          const successfulUploads = uploadResults.filter(result => result.statusCode === 201)
          const failedUploads = uploadResults.length - successfulUploads.length

          if (successfulUploads.length > 0) {
            setUploadedFiles(successfulUploads.map(result => result.data))
          }

          if (failedUploads > 0) {
            setSuccessMessage(`Concern saved. ${failedUploads} attachment${failedUploads === 1 ? '' : 's'} could not be uploaded; you can add them from the case details.`)
          } else {
            setSuccessMessage('Concern raised successfully.')
          }
        }

        // Show success message and auto-navigate
        if (onSuccess) onSuccess()
        setShowSuccessModal(true)
        setTimeout(() => {
          setShowSuccessModal(false)
          onTabChange('list')
        }, 2000)
      } else {
        // Handle rate limit error specifically
        if (response.message === 'Rate limit exceeded') {
          setError('Rate limit exceeded')
        } else {
          setError(response.message || 'Failed to create incident')
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create incident')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onTabChange('list')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const newFiles = Array.from(files)
      const slots = Math.max(0, 5 - attachments.length)
      const acceptedFiles = newFiles.slice(0, slots)
      if (newFiles.length > slots) {
        setFileError('Maximum of 5 files allowed per submission. Please remove some files before adding more.')
      } else {
        setFileError(null)
      }
      setAttachments(prev => [...prev, ...acceptedFiles])
      // Clear the native selection; the retained files are managed in React state.
      e.target.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
    setFileError(null)
  }

  const handleRemoveUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="incident-form">
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Case ID *</Form.Label>
            <Form.Control
              type="text"
              name="caseId"
              value={formData.caseId}
              readOnly
            />
                <Form.Text className="text-muted">
                  Case ID is automatically generated
                </Form.Text>
              </Form.Group>
            </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Incident Category *</Form.Label>
              <Form.Select
                name="categoryCode"
                value={formData.categoryCode}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.code} value={category.code}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Incident *</Form.Label>
              <Form.Control
                type="date"
                name="incidentDate"
                value={formData.incidentDate || ''}
                onChange={handleChange}
                required
                isInvalid={!!dateError}
                max={today}
              />
              {dateError && (
                <Form.Text className="text-danger">
                  {dateError}
                </Form.Text>
              )}
              <Form.Text className="text-muted">
                Only today or past dates are allowed
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Incident Description *</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={5}
            placeholder="Provide detailed description of the incident..."
            required
            minLength={50}
              isInvalid={!!descriptionError}
          />
          {descriptionError ? <Form.Control.Feedback type="invalid">{descriptionError}</Form.Control.Feedback> : <Form.Text className="text-muted">
            Minimum 50 characters required
          </Form.Text>}
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>People Involved</Form.Label>
              <Form.Control
                type="text"
                name="peopleInvolved"
                value={formData.peopleInvolved}
                onChange={handleChange}
                placeholder="John Doe, Jane Smith"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Location Or Context</Form.Label>
              <Form.Control
                type="text"
                name="locationContext"
                value={formData.locationContext}
                onChange={handleChange}
                placeholder="Office premises, Floor 3"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>

              <Form.Select
                name="assignedToId"
                value={formData.assignedToId}
                onChange={handleChange}
              >
                <option value="">Select User</option>

                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                    {user.employeeCode ? ` (${user.employeeCode})` : ''}
                  </option>
                ))}
              </Form.Select>

              <Form.Text className="text-muted">
                Select an eligible active HR or Admin user. POSH assignment is handled by an authorized HR/Admin user.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="anonymousSubmission"
            name="isAnonymous"
            checked={formData.isAnonymous}
            onChange={handleChange}
            label="Anonymous Submission"
          />
          <Form.Text className="text-muted">
            {formData.isAnonymous
              ? 'Your identity will be hidden from unauthorized viewers.'
              : 'Your identity will be visible to authorized users.'}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Attachments</Form.Label>
          <Form.Control
            type="file"
            multiple
            onChange={handleFileChange}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            disabled={attachments.length >= 5}
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <Form.Text className="text-muted mb-0">
              Supported formats: Images, PDF, Word, Excel, Text files
            </Form.Text>
            <Badge 
              bg={attachments.length >= 5 ? 'danger' : attachments.length >= 4 ? 'warning' : 'info'}
              className="mb-0"
            >
              {attachments.length}/5 files
            </Badge>
          </div>
          
          {/* Progress bar for file limit */}
          <div className="mt-2" style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden' }}>
            <div 
              style={{ 
                height: '100%', 
                width: `${(attachments.length / 5) * 100}%`,
                backgroundColor: attachments.length >= 5 ? '#dc3545' : attachments.length >= 4 ? '#ffc107' : '#0d6efd',
                transition: 'width 0.3s ease, background-color 0.3s ease'
              }}
            />
          </div>
          
          {fileError && (
            <Form.Text className="text-danger d-block mt-2">
              {fileError}
            </Form.Text>
          )}
          
          {attachments.length >= 5 && (
            <Form.Text className="text-warning d-block mt-2">
              Maximum file limit reached. Remove files to add more.
            </Form.Text>
          )}
        </Form.Group>

        {(attachments.length > 0 || uploadedFiles.length > 0) && (
          <div className="mb-3">
            <Form.Label className="mb-2">Selected Files</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <Badge
                  key={index}
                  bg="light"
                  text="dark"
                  className="d-flex align-items-center gap-2 px-3 py-2 position-relative"
                  style={{ cursor: 'default' }}
                >
                  <span>{file.name}</span>
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    ({formatFileSize(file.size)})
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger border-0 ms-1 p-0"
                    onClick={() => handleRemoveFile(index)}
                    style={{ 
                      fontSize: '0.75rem',
                      minWidth: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      opacity: 0.7,
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                    title="Remove file"
                  >
                    ×
                  </button>
                </Badge>
              ))}
              {uploadedFiles.map((file, index) => (
                <Badge
                  key={`uploaded-${index}`}
                  bg="success"
                  className="d-flex align-items-center gap-2 px-3 py-2"
                >
                  <span>{file.fileName}</span>
                  <button
                    type="button"
                    className="btn-close btn-close-sm ms-1"
                    onClick={() => handleRemoveUploadedFile(index)}
                    style={{ fontSize: '0.5rem' }}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mb-3">{error}</div>
        )}

        {/* <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="declarationCheckbox"
            checked={declarationAccepted}
            onChange={(e) => {
              setDeclarationAccepted(e.target.checked)
              if (e.target.checked) {
                setDeclarationError(null)
              }
            }}
            label="I confirm that the information provided is accurate to the best of my knowledge."
          />
          {declarationError && (
            <Form.Text className="text-danger">
              {declarationError}
            </Form.Text>
          )}
        </Form.Group> */}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="declarationCheckbox"
            checked={declarationAccepted}
            onChange={(e) => {
              setDeclarationAccepted(e.target.checked)

              if (e.target.checked) {
                setDeclarationError(null)
              }
            }}
            label="I confirm that the information provided is accurate to the best of my knowledge."
          />

          {declarationError && (
            <div className="text-danger mt-2">
              {declarationError}
            </div>
          )}
        </Form.Group>

        <div className="d-flex gap-2">
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Submit Concern'}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
        </div>
      </Form>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Concern Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to submit this Concern? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmSubmit} disabled={loading}>
            {loading ? 'Submitting...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <span style={{ fontSize: '3rem' }}>✅</span>
          </div>
          <h4 className="mb-2">{successMessage}</h4>
          <p className="text-muted mb-0">Redirecting to Concerns list...</p>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default IncidentForm
